/**
 * 2026-09-22 — Genel Bakış sorun / alarm metinleri.
 *
 * The server emits only a stable code + params (GET /admin/overview problems[],
 * GET /admin/overview/alarms alarms[]); every operator-facing sentence lives
 * here, in Turkish. An unknown future code still renders (generic text + the
 * raw code) instead of disappearing.
 */
import type {
  GatewayHealthStatus,
  GatewaySignalSource,
  OverviewSeverity,
  ProblemCode,
  ProblemParams,
  ProviderHealthStatus,
  ResidentialStatus,
} from '../api/overview.js';
import { providerLabel, providerLabels } from '../providers/catalog.js';
import { loginFailureLabel } from './login-failure-reasons.js';

export interface ProblemText {
  readonly title: string;
  readonly description: string;
}

export const SEVERITY_LABEL: Readonly<Record<OverviewSeverity, string>> = {
  critical: 'Kritik',
  warn: 'Uyarı',
  info: 'Bilgi',
};

export const PROVIDER_STATUS_LABEL: Readonly<Record<ProviderHealthStatus, string>> = {
  ok: 'Sağlıklı',
  warn: 'Dikkat',
  critical: 'Kritik',
  idle: 'Boşta',
};

export const GATEWAY_STATUS_LABEL: Readonly<Record<GatewayHealthStatus, string>> = {
  ok: 'Çalışıyor',
  down: 'Çalışmıyor',
  unknown: 'Bilinmiyor',
};

export const GATEWAY_SOURCE_LABEL: Readonly<Record<GatewaySignalSource, string>> = {
  canary: 'bağlantı kontrolü',
  probes: 'giriş denemeleri',
  none: 'sinyal yok',
};

export const RESIDENTIAL_STATUS_LABEL: Readonly<Record<ResidentialStatus, string>> = {
  ok: 'Bağlantı doğrulandı',
  down: 'Bağlantı başarısız',
  unconfigured: 'Yapılandırılmamış',
  unknown: 'Bilinmiyor',
};

function fmt(v: string | number | null | undefined): string {
  if (typeof v === 'number') return v.toLocaleString('tr-TR');
  if (typeof v === 'string' && v.length > 0) return v;
  return '?';
}

function scopeLabel(providerId: string | null | undefined): string {
  return providerId ? providerLabel(providerId) : '';
}

/** Non-empty string param, else null. */
function text(v: string | number | null | undefined): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

/** Turkish percent ("%12"); the server sends an integer 0..100. */
function pct(v: string | number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? `%${Math.round(v)}` : '%?';
}

/** "<Sağlayıcı>: cümle" — or the sentence alone, capitalised, without a subject. */
function lead(subject: string, sentence: string): string {
  if (subject) return `${subject}: ${sentence}`;
  return sentence.charAt(0).toLocaleUpperCase('tr-TR') + sentence.slice(1);
}

/**
 * Turkish title + one-sentence description for a problem/alarm code.
 * `providerId` (problems) or the alarm's first provider supplies the subject
 * where the params do not.
 */
export function describeProblem(
  code: string,
  params: ProblemParams,
  providerId?: string | null,
): ProblemText {
  const subject = scopeLabel(providerId);
  switch (code as ProblemCode) {
    case 'gateway_down': {
      const who = providerLabels(typeof params.providers === 'string' ? params.providers : null) || subject;
      const host = typeof params.host === 'string' && params.host.length > 0 ? params.host : null;
      return {
        title: 'Konut proxy ağ geçidi çalışmıyor',
        description:
          `${who ? `${who} için ` : ''}${host ? `${host} ` : 'ağ geçidi '}bağlantı kurmuyor; ` +
          'bu sağlayıcılarda hesap girişi ve mail teslimatı durur. Proxy ayarını/aboneliğini kontrol edin.',
      };
    }
    case 'residential_gateway_unconfigured':
      return {
        title: 'Ortak residential proxy yapılandırılmamış',
        description: 'Tüm sağlayıcılar kayıtlı ortak gateway kullanır. Yeni bağlantıların devam etmesi için gateway ayarını kaydedin.',
      };
    case 'runtime_metrics_stale':
      // Server: instant `up` of the runtime scrape target is < 1 or absent — no time window.
      return {
        title: 'Çalışma zamanı ölçümleri gelmiyor',
        description:
          "Mail çalışma zamanı pod'u şu an Prometheus'a ölçüm vermiyor (scrape hedefi kapalı); giriş ve ağ geçidi sinyalleri eksik olabilir.",
      };
    case 'metrics_unavailable':
      return {
        title: 'Ölçüm servisine ulaşılamıyor',
        description:
          'Prometheus sorgusu başarısız. Ölçüme dayalı kontroller "bilinmiyor" gösteriliyor; bu "sağlıklı" anlamına gelmez.',
      };
    case 'provider_no_delivery':
      return {
        title: `${subject ? `${subject}: ` : ''}Teslimat yok`,
        description: `Son 24 saatte ${fmt(params.orders24h)} sipariş geldi ama hiçbiri teslim edilmedi.`,
      };
    case 'provider_sellable_zero':
      return {
        title: `${subject ? `${subject}: ` : ''}Satılabilir stok yok`,
        description: 'Satışta görünüyor ama satılabilir kutu/alias kalmadı; gelen siparişler karşılanamaz.',
      };
    case 'provider_stale_verification':
      return {
        title: `${subject ? `${subject}: ` : ''}Hesap doğrulaması bayat`,
        // hours === null: no active-session account has a check timestamp at all.
        description:
          typeof params.hours === 'number'
            ? `Son ${fmt(params.hours)} saattir başarılı hesap kontrolü yok; giriş ya da proxy sorunu olabilir.`
            : 'Hiç başarılı hesap kontrolü kaydı yok (doğrulanmış oturumlu hesap bulunmuyor); giriş ya da proxy sorunu olabilir.',
      };
    case 'provider_sticky_quarantine': {
      const top = typeof params.topReason === 'string' && params.topReason.length > 0
        ? ` En sık neden: ${loginFailureLabel(params.topReason).label}.`
        : '';
      return {
        title: `${subject ? `${subject}: ` : ''}Kalıcı karantinada hesaplar`,
        description:
          `${fmt(params.count)} hesap 10 deneme sınırına ulaştı ve artık otomatik denenmiyor.${top}`,
      };
    }
    case 'provider_stock_gap':
      return {
        title: `${subject ? `${subject}: ` : ''}Gösterilen stok satılabilirden fazla`,
        description:
          `Müşteriye ${fmt(params.displayed)} gösteriliyor ama yalnızca ${fmt(params.sellable)} satılabilir; ` +
          'fark hatalı girişli veya doğrulanmamış hesaplardan geliyor.',
      };
    case 'stuck_orders':
      return {
        title: 'Takılı siparişler',
        description:
          `${fmt(params.expiredLease)} siparişin kira süresi dolmuş ama kapanmamış; ` +
          `${fmt(params.oldReserved)} sipariş mail aldığı hâlde 15 dakikadan uzun süredir açık ` +
          '(sonlandırma/olay hattı takılmış olabilir).',
      };
    case 'notifier_unconfigured':
      return {
        title: 'Alarm e-postası bağlı değil',
        description:
          'Alarmlar yalnızca bu panelde görünür, kimseye e-posta gitmez. Bildirim adresi (OPS_ALERT_EMAIL_TO) ayarlanmalı.',
      };
    case 'evaluator_silent':
      return {
        title: 'Alarm servisi yanıt vermiyor',
        description:
          typeof params.minutes === 'number'
            ? `Alarm değerlendiricisi ${fmt(params.minutes)} dakikadır çalışmadı; yeni sorunlar fark edilmeyebilir ve e-posta gitmez.`
            : 'Alarm değerlendiricisinin çalıştığına dair kayıt yok; yeni sorunlar fark edilmeyebilir ve e-posta gitmez.',
      };
    // 2026-09-23 — display-only codes (never alarm e-mails). The provider is
    // shown as a badge next to these titles, so the titles stay short.
    case 'provider_low_delivery':
      return {
        title: 'Teslim oranı düşük',
        description: lead(
          subject,
          `son 24 saatte bitmiş ${fmt(params.finished)} siparişin yalnızca ${fmt(params.delivered)} tanesi ` +
            `teslim edildi (${pct(params.ratePct)}).`,
        ),
      };
    case 'site_low_delivery': {
      const site = text(params.site) ?? 'Bilinmeyen site';
      return {
        title: `${site} siparişlerinde teslim oranı düşük`,
        description:
          lead(
            subject,
            `son 24 saatte bitmiş ${fmt(params.finished)} ${site} siparişinin yalnızca ` +
              `${fmt(params.delivered)} tanesi teslim edildi (${pct(params.ratePct)}).`,
          ) + ' Bu site, bu sağlayıcının adreslerine mail göndermiyor olabilir.',
      };
    }
    case 'customer_low_delivery': {
      const who = text(params.email) ?? text(params.actorId) ?? 'Bilinmeyen müşteri';
      const top = text(params.topSite);
      return {
        title: 'Müşterinin siparişleri teslim edilemiyor',
        description:
          `${who}: son 24 saatte bitmiş ${fmt(params.finished)} siparişinin yalnızca ` +
          `${fmt(params.delivered)} tanesi teslim edildi (${pct(params.ratePct)}).` +
          (top ? ` En çok sipariş verdiği site: ${top}.` : ''),
      };
    }
    case 'provider_login_failed':
      return {
        title: 'Girişi başarısız hesaplar',
        description: lead(
          subject,
          `${fmt(params.count)} hesap giriş yapamadığı için karantinada (toplam ${fmt(params.total)} hesap). ` +
            'Henüz 10 deneme sınırına ulaşmadılar, yani kalıcı karantinada değiller.',
        ),
      };
    case 'provider_disabled_accounts':
      return {
        title: 'Devre dışı hesaplar',
        description: lead(
          subject,
          `${fmt(params.count)} hesap devre dışı (toplam ${fmt(params.total)} hesap). ` +
            'Bu hesaplar satışa katılmaz; nedenini sağlayıcı sayfasından kontrol edin.',
        ),
      };
    case 'provider_low_stock':
      return {
        title: 'Satılabilir stok azaldı',
        description: lead(
          subject,
          `yalnızca ${fmt(params.sellable)} satılabilir kutu/alias kaldı. Stok biterse gelen siparişler karşılanamaz.`,
        ),
      };
    case 'provider_renewal_failing':
      return {
        title: 'Alias yenileme hataları',
        description: lead(
          subject,
          `son 24 saatte ${fmt(params.failed)} alias yenilemesi başarısız oldu, yalnızca ${fmt(params.completed)} ` +
            'tanesi tamamlandı. Yenileme aksarsa satılabilir alias stoğu azalabilir.',
        ),
      };
    case 'provider_renewal_stuck': {
      const minutes = typeof params.minutes === 'number' && params.minutes > 0 ? params.minutes : 30;
      const session =
        typeof params.sessionNotActive === 'number' && params.sessionNotActive > 0
          ? ` ${fmt(params.sessionNotActive)} tanesinin oturumu aktif değil (yenileme bunları seçmez).`
          : '';
      const cooldown =
        typeof params.inCooldown === 'number' && params.inCooldown > 0
          ? ` Ayrıca ${fmt(params.inCooldown)} parent bekleme süresinde (sayılmadı).`
          : '';
      return {
        title: 'Biten parentlar yenilenmiyor',
        description: lead(
          subject,
          `${fmt(params.parents)} parent'ın bütün aliasları kullanıldı ama ${fmt(minutes)} dakikadır yenilemeye ` +
            `alınmadı.${session}${cooldown} Yenileme zamanlayıcısını kontrol edin.`,
        ),
      };
    }
    case 'notifier_not_configured':
      return {
        title: 'Alarm e-postası ayarlı değil',
        description:
          'Alarmlar yalnızca bu panelde görünür, kimseye e-posta gitmez. Bildirim adresi (OPS_ALERT_EMAIL_TO) ayarlanmalı.',
      };
    // 2026-09-23 — delivery-incident ALARMS (they page; also shown in the
    // alarm banner, where the customer e-mail is never present).
    case 'site_family_no_delivery': {
      const site = text(params.site) ?? 'Bilinmeyen site';
      const domain = text(params.domain) ?? 'bu alan adı';
      const evidence =
        params.rule === 'streak'
          ? `art arda ${fmt(params.streak)} sipariş mail gelmeden süresi doldu`
          : `son 1 saatte biten ${fmt(params.finished)} siparişin yalnızca ${fmt(params.delivered)} tanesi ` +
            `teslim edildi (${pct(params.ratePct)})`;
      const cause =
        params.classification === 'site_side'
          ? ` Aynı kutulara başka sitelerin mailleri geliyor; ${site} bu alan adına mail göndermiyor.`
          : typeof params.controlFinished === 'number' && params.controlFinished > 0
            ? ' Aynı kutulara başka sitelerden de mail gelmedi; sorun bizim tarafta (gelen kutusu okuma) olabilir.'
            : ' Aynı kutularda karşılaştırılacak başka site siparişi yok.';
      const alt = text(params.bestAlternativeDomain);
      return {
        title: `${site}: ${domain} adreslerine mail gelmiyor`,
        description:
          lead(subject, `${evidence}.`) +
          cause +
          (alt
            ? ` Müşterilere ${alt} önerin (son 24 saatte ${pct(params.bestAlternativeRatePct)} teslim).`
            : ' Son 24 saatte bu site için iyi çalışan başka alan adı yok.') +
          ' Satış durdurulmadı.',
      };
    }
    case 'gmx_imap_bad_sellable': {
      // "sold" (server-measured: orders placed after the rejection) is the
      // only basis for saying the boxes are still being sold.
      const sold = typeof params.sold === 'number' && params.sold > 0 ? params.sold : 0;
      return {
        title: sold > 0 ? 'IMAP girişi reddedilen kutulara sipariş veriliyor' : 'IMAP girişi reddedilen kutular hâlâ açık',
        description: lead(
          subject || providerLabel('gmx'),
          `${fmt(params.parents)} kutunun IMAP girişi reddediliyor ama kutular açık (${fmt(params.aliases)} satılabilir alias). ` +
            (sold > 0
              ? `${fmt(sold)} tanesine giriş reddedildikten sonra yine sipariş verildi; bu adreslere gelen mailler okunamaz. ` +
                'Bu kutuları hemen satıştan çekin.'
              : 'Sistem şimdilik bunlara sipariş vermiyor ama kutular kapatılmadı. Kontrol edip satıştan çekin.'),
        ),
      };
    }
    case 'customer_zero_yield_loop': {
      const who = text(params.email) ?? text(params.actorId) ?? 'Bilinmeyen müşteri';
      const domain = text(params.domain);
      return {
        title: 'Müşteri art arda mail alamıyor',
        description:
          `${who}: ${text(params.site) ?? 'bilinmeyen site'} siparişlerinde art arda ${fmt(params.count)} sipariş ` +
          `mail gelmeden süresi doldu${domain ? ` (${domain})` : ''}. Müşteriye ulaşıp başka alan adı önerin.`,
      };
    }
    case 'inbox_listing_failing':
      return {
        title: 'Gelen kutusu okunamıyor',
        description: lead(
          subject,
          `son 10 dakikadaki ${fmt(params.total)} listelemenin ${fmt(params.failed)} tanesi hata verdi ` +
            `(${pct(params.failPct)}); bu sürede mail teslim edilemez.`,
        ),
      };
    case 'sender_skip_high': {
      const site = text(params.site) ?? 'Bilinmeyen site';
      return {
        title: `${site} mailleri gönderen kontrolüne takılıyor`,
        description: lead(
          subject,
          `son 30 dakikada ${fmt(params.skips)} mail atlandı, ${fmt(params.delivered)} sipariş teslim edildi. ` +
            'Sitenin gönderen adresi farklı olabilir; gönderen eşleme listesini kontrol edin.',
        ),
      };
    }
    // 2026-09-28 (D-243) — the provider-level delivery rule and per-domain stock.
    case 'provider_no_mail_1h': {
      const minutes = typeof params.windowMinutes === 'number' ? params.windowMinutes : 60;
      return {
        title: `${subject ? `${subject}: ` : ''}1 saattir hiç mail gelmiyor`,
        description:
          `Son ${fmt(minutes)} dakikada açılan ${fmt(params.orders)} siparişin (${fmt(params.sites)} farklı site, ` +
          `${fmt(params.customers)} müşteri) hiçbirine mail gelmedi. Sorun tek bir sitede değil; hesap girişi, ` +
          'gelen kutusu okuma ya da proxy kontrol edilmeli.',
      };
    }
    case 'domain_stock_out': {
      const domain = text(params.domain) ?? 'Alan adı';
      return {
        title: `${domain} stoğu bitti`,
        description: lead(
          subject,
          `satışta ama ayrılabilir adres kalmadı; son 24 saatte ${fmt(params.orders24h)} sipariş ` +
            `(en yoğun saatte ${fmt(params.peakHour24h)}). Müşteriler bu alan adından sipariş veremiyor; ` +
            'stok ekleyin ya da yenilemeyi kontrol edin.',
        ),
      };
    }
    case 'domain_low_stock': {
      const domain = text(params.domain) ?? 'Alan adı';
      const cover = typeof params.coverHours === 'number' ? ` (bu hızla ~${fmt(params.coverHours)} saat yeter)` : '';
      return {
        title: `${domain} stoğu azaldı`,
        description: lead(
          subject,
          `${fmt(params.reservable)} ayrılabilir adres kaldı${cover}; son 24 saatte ${fmt(params.orders24h)} sipariş, ` +
            `en yoğun saatte ${fmt(params.peakHour24h)}.`,
        ),
      };
    }
    default:
      return {
        title: `${subject ? `${subject}: ` : ''}Tanımsız sorun`,
        description: `Sunucu bu panelin tanımadığı bir sorun kodu döndürdü (${code}).`,
      };
  }
}

/** Retry-problematic error codes → Turkish. */
export function retryErrorText(reason: string): string {
  if (reason === 'forbidden') return 'Bu işlem için yetkiniz yok (provider:manage_inventory).';
  // requireSiteScoped (gmx/mailcom) answers 404 {error:'provider_not_supported',
  // detail:'site_scoped_rights_required'}; liftServerCode prefers `detail`.
  if (
    reason === 'not_found' ||
    reason === 'http_404' ||
    reason === 'provider_unknown' ||
    reason === 'provider_not_supported' ||
    reason === 'site_scoped_rights_required'
  ) {
    return 'Bu sağlayıcı toplu yeniden denemeyi desteklemiyor.';
  }
  if (reason === 'postgres_unavailable' || reason === 'service_unavailable' || reason === 'http_503') {
    return 'Veritabanına şu an ulaşılamıyor; biraz sonra tekrar deneyin.';
  }
  if (reason.startsWith('network:')) return 'Sunucuya ulaşılamadı; bağlantıyı kontrol edip tekrar deneyin.';
  if (reason === 'credential_vault_not_configured') return 'Kimlik kasası yapılandırılmamış; deneme başlatılamaz.';
  if (reason === 'empty_response_body' || reason === 'invalid_retry_payload') {
    return 'Sunucu beklenmeyen bir yanıt döndü (yönlendirme eksik olabilir).';
  }
  return `İşlem başarısız (${reason}).`;
}

/** 2026-09-28 (D-243) — incident mark (Okundu / İncelendi / Ertele) error codes → Turkish. */
export function incidentActionErrorText(reason: string): string {
  if (reason === 'forbidden') return 'Bu işlem için yetkiniz yok (provider:inspect).';
  if (reason === 'alarm_store_unavailable' || reason === 'http_503') {
    return 'Olay kaydı şu an kullanılamıyor (tablo eksik ya da veritabanına ulaşılamıyor); işaret kaydedilmedi.';
  }
  if (reason === 'incident_resolved') return 'Bu olay zaten düzelmiş; okundu / ertele gerekmiyor.';
  if (reason === 'incident_unknown' || reason === 'not_found' || reason === 'http_404') {
    return 'Olay bulunamadı (kapanmış olabilir); liste bir sonraki yenilemede güncellenir.';
  }
  if (reason === 'note_too_long') return 'Not en fazla 500 karakter olabilir.';
  if (reason === 'note_invalid' || reason === 'snooze_minutes_invalid' || reason === 'incident_id_invalid') {
    return 'Geçersiz istek; sayfayı yenileyip tekrar deneyin.';
  }
  if (reason === 'auth_required') return 'Oturum sona erdi; tekrar giriş yapın.';
  if (reason.startsWith('network:') || reason === 'network') return 'Sunucuya ulaşılamadı; bağlantıyı kontrol edip tekrar deneyin.';
  return `İşlem başarısız (${reason}).`;
}

/** Overview load error codes → Turkish (the raw code stays in ErrorState details). */
export function overviewErrorHint(reason: string): string | null {
  if (reason === 'overview_unavailable' || reason === 'http_503') {
    return 'Genel bakış verisi şu an hesaplanamadı (veritabanı yavaş veya erişilemez olabilir); otomatik olarak tekrar denenecek.';
  }
  if (reason.startsWith('network:')) return 'Sunucuya ulaşılamadı; bağlantı geri gelince otomatik yenilenir.';
  if (reason === 'http_404' || reason === 'not_found') {
    return 'Genel bakış servisi (/admin/overview) sunucuda bulunamadı; API sürümü veya ingress yönlendirmesi eksik olabilir.';
  }
  if (reason === 'empty_response_body' || reason === 'invalid_overview_payload') {
    return 'Sunucu beklenen formatta yanıt vermedi; /admin/overview ingress yönlendirmesini kontrol edin.';
  }
  return null;
}
